import { api } from "@/shared/services/api";
import type { ChatSession } from "@/features/core-app/interfaces";

export interface Project {
  id: number;
  name: string;
  description?: string;
  instructions?: string;
  chat_sessions?: ChatSession[];
}

export interface ProjectFile {
  id: string;
  file_id?: string;
  name?: string;
  project_id?: number | null;
  user_id?: string | null;
  created_at?: string;
  status?: string;
  file_type?: string;
  last_accessed_at?: string;
  chat_file_type?: string;
  token_count?: number | null;
  chunk_count?: number | null;
  temp_id?: string;
}

export interface CategorizedFiles {
  user_files?: ProjectFile[];
  rejected_files?: { file_name: string; reason: string }[];
}

export interface UserFileDeleteResult {
  has_associations: boolean;
}

export interface ProjectDetails {
  project: Project;
  files: ProjectFile[];
}

// Backend response shape — see project_handler.go's projectResponse/projectFileResponse.
interface BackendProjectResponse {
  id: number;
  name: string;
  description?: string;
  instructions?: string;
  chat_sessions?: { id: string; name: string; time_updated?: string }[];
}

function toProject(p: BackendProjectResponse): Project {
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    instructions: p.instructions,
    chat_sessions: p.chat_sessions?.map((cs) => ({
      id: cs.id,
      name: cs.name,
      time_created: "",
      time_updated: cs.time_updated,
    })),
  };
}

export async function getProjects(): Promise<Project[]> {
  const rows = await api.get("user/projects").json<BackendProjectResponse[]>();
  return rows.map(toProject);
}
export const fetchProjects = getProjects;

export async function createProject(
  name: string,
  description?: string
): Promise<Project> {
  const p = await api
    .post("user/projects", { json: { name, description } })
    .json<BackendProjectResponse>();
  return toProject(p);
}

// Real call site (ChatSessionMorePopup.tsx) passes (targetProjectId, chatSession.id) —
// projectId first, chatId second.
export async function moveChatSession(
  projectId: number | string,
  chatId: string
): Promise<boolean> {
  try {
    await api.put(`user/projects/${projectId}/chat-sessions/${chatId}`);
    return true;
  } catch {
    return false;
  }
}

export async function removeChatSessionFromProject(
  chatId: string
): Promise<boolean> {
  try {
    await api.delete(`user/projects/chat-sessions/${chatId}`);
    return true;
  } catch {
    return false;
  }
}

export async function getProjectTokenCount(
  projectId: number | string
): Promise<number> {
  try {
    const res = await api
      .get(`user/projects/${projectId}/token-count`)
      .json<{ total_tokens: number }>();
    return res.total_tokens ?? 0;
  } catch {
    return 0;
  }
}

export enum UserFileStatus {
  PROCESSING = "processing",
  UPLOADING = "uploading",
  DELETING = "deleting",
  COMPLETED = "completed",
}

export async function unlinkFileFromProject(
  projectId: number,
  fileId: string
): Promise<{ ok: boolean }> {
  try {
    await api.delete(`user/projects/${projectId}/files/${fileId}`);
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

export async function linkFileToProject(
  projectId: number,
  fileId: string
): Promise<{ ok: boolean }> {
  try {
    await api.post(`user/projects/${projectId}/files/${fileId}`);
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

export async function getUserFileStatuses(
  fileIds: string[]
): Promise<ProjectFile[]> {
  if (fileIds.length === 0) return [];
  return api
    .post("user/projects/files/statuses", { json: { file_ids: fileIds } })
    .json<ProjectFile[]>();
}

// Field name/shape matches ProjectsContext.tsx's beginUpload(): builds a
// `${size}|${namePrefix(50)}` -> tempId map so the backend can echo back
// `temp_id` per uploaded file for optimistic-UI reconciliation.
export async function uploadFiles(
  files: File[],
  projectId?: number | null,
  tempIdMap?: Map<string, string>
): Promise<CategorizedFiles> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  if (tempIdMap && tempIdMap.size > 0) {
    formData.append(
      "temp_id_map",
      JSON.stringify(Object.fromEntries(tempIdMap))
    );
  }
  const path =
    projectId !== undefined && projectId !== null
      ? `user/projects/${projectId}/files`
      : "user/projects/files"; // no route without an id today; kept for signature compat
  return api.post(path, { body: formData }).json<CategorizedFiles>();
}

export async function getRecentFiles(): Promise<ProjectFile[]> {
  return api.get("user/projects/files/recent").json<ProjectFile[]>();
}

export async function getFilesInProject(
  projectId: number
): Promise<ProjectFile[]> {
  return api.get(`user/projects/${projectId}/files`).json<ProjectFile[]>();
}

export async function getProject(projectId: number): Promise<Project | null> {
  try {
    const p = await api
      .get(`user/projects/${projectId}`)
      .json<BackendProjectResponse>();
    return toProject(p);
  } catch {
    return null;
  }
}

export async function getProjectInstructions(
  projectId: number
): Promise<string> {
  const p = await getProject(projectId);
  return p?.instructions ?? "";
}

export async function upsertProjectInstructions(
  projectId: number,
  instructions: string
): Promise<void> {
  await api.patch(`user/projects/${projectId}`, { json: { instructions } });
}

export async function getProjectDetails(
  projectId: number
): Promise<ProjectDetails> {
  const [project, files] = await Promise.all([
    getProject(projectId),
    getFilesInProject(projectId).catch(() => []),
  ]);
  return { project: project ?? { id: projectId, name: "" }, files };
}

export async function renameProject(
  projectId: number,
  name: string
): Promise<Project> {
  const p = await api
    .patch(`user/projects/${projectId}`, { json: { name } })
    .json<BackendProjectResponse>();
  return toProject(p);
}

export async function deleteProject(projectId: number): Promise<void> {
  await api.delete(`user/projects/${projectId}`);
}

export async function deleteUserFile(
  fileId: string
): Promise<UserFileDeleteResult> {
  return api
    .delete(`user/projects/files/${fileId}`)
    .json<UserFileDeleteResult>();
}

export async function getSessionProjectTokenCount(
  chatSessionId: string
): Promise<number> {
  const response = await fetch(
    `/api/user/projects/session/${encodeURIComponent(chatSessionId)}/token-count`
  );
  if (!response.ok) return 0;
  const data = (await response.json()) as { total_tokens: number };
  return data.total_tokens ?? 0;
}

export async function getProjectFilesForSession(
  chatSessionId: string
): Promise<ProjectFile[]> {
  const response = await fetch(
    `/api/user/projects/session/${encodeURIComponent(chatSessionId)}/files`
  );
  if (!response.ok) return [];
  return response.json();
}

export async function getMaxSelectedDocumentTokens(
  personaId: number
): Promise<number | null> {
  const response = await fetch(
    `/api/chat/max-selected-document-tokens?persona_id=${personaId}`
  );
  if (!response.ok) return null;
  const json = await response.json();
  return (json?.max_tokens as number) ?? null;
}
